// @flow

import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTheme } from '@mui/material/styles';
import * as R from 'ramda';
import { log } from '../../utils/jsUtils';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';

import EditIcon from '@mui/icons-material/Edit';
import RemoveIcon from '@mui/icons-material/DeleteOutline';

import TagRenderer from '../SharedComponents/TagRenderer';
import ConfirmRemoveTagDialog from './ConfirmRemoveTagDialog';
import baseTableStyles from '../Styles/BaseTableStyles.Style';
import EditTagDialog from './EditTagDialog';
import ConfigurableTable from '../SharedComponents/ConfigurableTable';
import ContentContainer from '../SharedComponents/ContentContainer';
import SPBackend from '../../services/SPBackend';

import { createClasses } from '../../utils/stylesUtils';
import tagsContentStyles from './Styles/TagsContent.Style';
import commonListTableStyles from '../Styles/CommonListTable.Style';

import {
  selectAllTags,
  selectTagSortInfo,
  setSortInfo,
  fetchTags
} from '../../reduxStore/modules/tags';

import { selectUser } from '../../reduxStore/modules/user';
import { useEditTagMutation, useArchiveTagMutation } from '../../features/api/tagsApi';

import { useI18n } from '../../contexts/I18nContext';

// Table configuration for Tags
const TAGS_TABLE_CONFIG = {
  columns: [
    {
      key: 'name',
      headerLabel: 'Tag Name',
      flex: '1 1 auto',
      minWidth: 200,
      sortable: true
    },
    {
      key: 'customerCount',
      headerLabel: 'Customers',
      width: 200,
      flex: '0 0 auto',
      sortable: true
    }
  ],
  actions: [
    {
      key: 'edit',
      icon: EditIcon,
      label: 'Edit Tag',
      onClick: 'handleEditTag'
    },
    {
      key: 'remove',
      icon: RemoveIcon,
      label: 'Remove Tag',
      onClick: 'handleRemoveTag'
    }
  ],
  defaultSort: 'name',
  enableMultiSelect: false
};

const TagsContent = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const tags = useSelector(selectAllTags);
  const user = useSelector(selectUser);
  const sortInfo = useSelector(selectTagSortInfo);
  const i18n = useI18n();
  
  // RTK Query mutations
  const [editTag, { isLoading: isEditingTag }] = useEditTagMutation();
  const [archiveTagMutation, { isLoading: isArchivingTag }] = useArchiveTagMutation();
  
  log('[TagsContent] tags: ', tags);

  const [selectedTag, setSelectedTag] = useState(null);
  const [openConfirmRemoveTagDialog, setOpenConfirmRemoveTagDialog] = useState(false);
  const [openEditTagDialog, setOpenEditTagDialog] = useState(false);

  // Fixed the useEffect to depend on both user and sortInfo
  const initialMount = React.useRef(true);

  useEffect(() => {
    if (user) {
      // On initial mount, always fetch tags
      if (initialMount.current) {
        initialMount.current = false;
        dispatch(fetchTags(user, sortInfo));
      } else {
        // After mount, only fetch when sortInfo changes and we need to re-sort
        if (sortInfo) {
          dispatch(fetchTags(user, sortInfo));
        }
      }
    }
  }, [user, sortInfo, dispatch]);

  // Handle sorting
  const handleSort = (columnKey, direction) => {
    const newOrder = {};
    newOrder[columnKey === 'name' ? 'Name' : 'Customers'] = direction;
    dispatch(setSortInfo(newOrder));
  };

  // Convert sortInfo to format expected by ConfigurableTable
  const convertedSortInfo = {};
  if (sortInfo) {
    if (sortInfo.Name !== undefined) {
      convertedSortInfo.name = sortInfo.Name;
    }
    if (sortInfo.Customers !== undefined) {
      convertedSortInfo.customerCount = sortInfo.Customers;
    }
  }

  // Action handlers
  const actionHandlers = {
    handleEditTag: (tag) => {
      setSelectedTag(tag);
      setOpenEditTagDialog(true);
    },
    handleRemoveTag: (tag) => {
      setSelectedTag(tag);
      setOpenConfirmRemoveTagDialog(true);
    }
  };

  // Cell renderer for each column
  const renderCell = (tag, column, index) => {
    switch (column.key) {
      case 'name':
        return (
          <TagRenderer
            readonly
            tagsIds={[tag._id]}
          />
        );
      case 'customerCount':
        return tag.Customers && tag.Customers.length > 0 ? tag.Customers.length : '-';
      default:
        return null;
    }
  };

  // Mobile item renderer
  const renderMobileItem = (tag, index) => (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <Box>
        <Typography variant="body1" sx={{ marginBottom: theme.spacing(0.5) }}>
          <TagRenderer
            readonly
            tagsIds={[tag._id]}
          />
        </Typography>
        <Typography 
          variant="body2" 
          color="textSecondary"
          sx={{
            fontSize: '0.8rem',
            marginTop: theme.spacing(0.5)
          }}
        >
          {tag.Customers && tag.Customers.length > 0 ? `${tag.Customers.length} Customers` : '0 Customers'}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', gap: theme.spacing(0.25) }}>
        <IconButton
          aria-label={`Edit ${i18n.t('tag')}`}
          sx={baseTableStyles.mobileActionButton(theme)}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            actionHandlers.handleEditTag(tag);
          }}
        >
          <EditIcon sx={{ fontSize: '1rem' }} />
        </IconButton>
        <IconButton
          aria-label={`Remove ${i18n.t('tag')}`}
          sx={baseTableStyles.mobileActionButton(theme)}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            actionHandlers.handleRemoveTag(tag);
          }}
        >
          <RemoveIcon sx={{ fontSize: '1rem' }} />
        </IconButton>
      </Box>
    </Box>
  );

  // Edit tag handler using RTK Query
  const handleEditTag = async (tagProps) => {
    log('[handleEditTag] tagProps: ', tagProps);
    
    if (!selectedTag) {
      log('[handleEditTag] No selected tag');
      return;
    }
    
    try {
      const tagData = {
        _id: selectedTag._id,
        ...R.pick(['_id', 'Name', 'Color'], tagProps)
      };
      
      await editTag({
        tagData,
        user,
        showNotification: true
      }).unwrap();
      
      log('[handleEditTag] Tag edit successful');
    } catch (error) {
      log('[handleEditTag] Error editing tag:', error);
      // Error handling is done in the RTK Query mutation
    }
  };

  // Archive tag handler
  // Archive tag function using RTK Query
  const archiveTag = async (tag) => {
    try {
      log('[TagsContent] Archiving tag:', tag);
      await archiveTagMutation({
        tagId: tag._id,
        user: user,
        showNotification: true
      }).unwrap();
      log('[TagsContent] Tag archived successfully');
    } catch (error) {
      log('[TagsContent] Error archiving tag:', error);
      // Error handling is done in the mutation itself
    }
  };

  return (
    <ContentContainer
      hasSubheader={true}
      sx={tagsContentStyles.tableContainer(theme)}
    >
      <ConfigurableTable
        data={tags || []}
        config={TAGS_TABLE_CONFIG}
        onSort={handleSort}
        sortInfo={convertedSortInfo}
        renderCell={renderCell}
        renderMobileItem={renderMobileItem}
        actionHandlers={actionHandlers}
        emptyMessage="No tags found"
      />

      <EditTagDialog
        actionConfirm={handleEditTag}
        open={openEditTagDialog}
        setOpen={setOpenEditTagDialog}
        tag={selectedTag}
      />
      <ConfirmRemoveTagDialog
        actionConfirm={archiveTag}
        open={openConfirmRemoveTagDialog}
        setOpen={setOpenConfirmRemoveTagDialog}
        tag={selectedTag}
      />
    </ContentContainer>
  );
};

export default TagsContent;

